import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { highlightMatch } from "@/lib/highlightText";
import {
  TERMS_PAGE_SIZES,
  useTerm,
  useTerms,
  wordFirstLetter,
} from "@/hooks/useTerms";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  BookOpen,
  Volume2,
  Share2,
  ExternalLink,
  Copy,
  Check,
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  Headphones,
} from "lucide-react";
import { toast } from "sonner";
import { ThemedAudioPlayer } from "@/components/ThemedAudioPlayer";
import { cn } from "@/lib/utils";

type Lang = "en" | "hi" | "gu";

// ─── Meaning renderer ────────────────────────────────────────────────────────

function renderMeaning(text: string, highlightQuery: string): ReactNode {
  const hl = (s: string): ReactNode =>
    highlightQuery ? highlightMatch(s, highlightQuery) : s;

  const isNumberedLine = (l: string) => /^\d+[.)]\s/.test(l);
  const blocks = text.split(/\n{2,}/);

  return (
    <div className="space-y-5">
      {blocks.map((block, bi) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        // Inline numbered list
        if (/\b1[.)]\s/.test(trimmed)) {
          const m = trimmed.match(/^([\s\S]*?)(?=\b1[.)]\s)/);
          const prefix = m ? m[1].trim() : "";
          const listPart = prefix ? trimmed.slice(prefix.length) : trimmed;
          const items = listPart.split(/\b\d+[.)]\s+/).filter(s => s.trim());

          if (items.length >= 2) {
            return (
              <div key={bi} className="space-y-3">
                {prefix && (
                  <p className="break-words leading-[1.85] text-foreground/90">
                    {hl(prefix)}
                  </p>
                )}
                <ol className="list-decimal space-y-2 pl-6 marker:font-semibold marker:text-primary">
                  {items.map((item, i) => (
                    <li key={i} className="break-words leading-[1.85] text-foreground/90 pl-1">
                      {hl(item.trim())}
                    </li>
                  ))}
                </ol>
              </div>
            );
          }
        }

        // Newline-separated numbered list
        const lines = trimmed.split("\n").map(l => l.trim()).filter(Boolean);
        const numberedCount = lines.filter(isNumberedLine).length;

        if (numberedCount >= 2 && numberedCount >= lines.length - 1) {
          const prefixLines = lines.filter(l => !isNumberedLine(l));
          const listLines = lines.filter(isNumberedLine);
          return (
            <div key={bi} className="space-y-3">
              {prefixLines.map((l, i) => (
                <p key={i} className="break-words leading-[1.85] text-foreground/90">{hl(l)}</p>
              ))}
              <ol className="list-decimal space-y-2 pl-6 marker:font-semibold marker:text-primary">
                {listLines.map((l, i) => (
                  <li key={i} className="break-words leading-[1.85] text-foreground/90 pl-1">
                    {hl(l.replace(/^\d+[.)]\s+/, "").trim())}
                  </li>
                ))}
              </ol>
            </div>
          );
        }

        // Regular paragraph
        return (
          <p key={bi} className="break-words leading-[1.85] text-foreground/90">
            {hl(trimmed)}
          </p>
        );
      })}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const TermDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightQuery = searchParams.get("highlight") ?? "";
  const { t } = useLanguage();

  const [activeLang, setActiveLang] = useState<Lang>("en");
  const [readingProgress, setReadingProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  const { data: term, isLoading } = useTerm(id || "");

  const { data: relatedTerms } = useTerms({
    letter: term?.word ? wordFirstLetter(term.word) : undefined,
    page: 1,
    page_size: TERMS_PAGE_SIZES.related,
    limit: TERMS_PAGE_SIZES.related,
  });

  const filteredRelatedTerms = relatedTerms?.filter(r => r.id !== id) ?? [];

  // Pick prev / next from the same-letter list
  const allSameLetter = relatedTerms ?? [];
  const currentIdx = allSameLetter.findIndex(r => r.id === id);
  const prevTerm = currentIdx > 0 ? allSameLetter[currentIdx - 1] : null;
  const nextTerm =
    currentIdx >= 0 && currentIdx < allSameLetter.length - 1
      ? allSameLetter[currentIdx + 1]
      : null;

  // Default active language to whichever meaning exists
  useEffect(() => {
    if (!term) return;
    if (term.meaning_english) setActiveLang("en");
    else if (term.meaning_hindi) setActiveLang("hi");
    else if (term.meaning_gujarati) setActiveLang("gu");
  }, [term?.id]);

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const total =
        document.documentElement.scrollHeight - window.innerHeight;
      setReadingProgress(total > 0 ? (scrolled / total) * 100 : 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleShare = async () => {
    try {
      await navigator.share({
        title: term?.word,
        text: term?.word ? t("termDetail.shareText", { word: term.word }) : "",
        url: window.location.href,
      });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success(t("termDetail.linkCopied"));
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success(t("termDetail.linkCopied"));
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-1 w-0 bg-primary/60" />
        <section className="bg-gradient-to-br from-[hsl(220,40%,13%)] to-[hsl(218,35%,22%)] py-16">
          <div className="container max-w-5xl space-y-4">
            <div className="h-8 w-32 animate-pulse rounded-lg bg-white/20" />
            <div className="h-12 w-80 animate-pulse rounded-lg bg-white/20" />
            <div className="h-5 w-48 animate-pulse rounded-lg bg-white/10" />
          </div>
        </section>
        <section className="py-12">
          <div className="container max-w-5xl">
            <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
              <div className="h-96 animate-pulse rounded-2xl bg-secondary/40" />
              <div className="space-y-4">
                <div className="h-48 animate-pulse rounded-2xl bg-secondary/40" />
                <div className="h-48 animate-pulse rounded-2xl bg-secondary/40" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ── Not found ──────────────────────────────────────────────────────────────
  if (!term) {
    return (
      <div className="from-secondary/30 to-background flex min-h-screen items-center justify-center bg-gradient-to-br">
        <div className="p-8 text-center">
          <div className="bg-muted mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full">
            <BookOpen className="text-muted-foreground h-12 w-12" />
          </div>
          <h1 className="text-foreground mb-3 font-serif text-3xl font-bold">
            {t("termDetail.notFound")}
          </h1>
          <p className="text-muted-foreground mb-8 max-w-md">
            {t("termDetail.notFoundBody")}
          </p>
          <Button
            onClick={() => navigate("/browse")}
            className="bg-primary hover:bg-primary/90 btn-shimmer"
          >
            {t("termDetail.browseAllTerms")}
          </Button>
        </div>
      </div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────
  const pronunciationAudioSrc =
    term.audio_url != null ? String(term.audio_url).trim() : "";

  const availableLangs: { key: Lang; label: string; native: string }[] = [
    { key: "en", label: "English", native: "English" },
    { key: "hi", label: "Hindi", native: "हिंदी" },
    { key: "gu", label: "Gujarati", native: "ગુજરાતી" },
  ].filter(l =>
    l.key === "en"
      ? !!term.meaning_english
      : l.key === "hi"
        ? !!term.meaning_hindi
        : !!term.meaning_gujarati
  );

  const activeMeaning =
    activeLang === "en"
      ? term.meaning_english
      : activeLang === "hi"
        ? term.meaning_hindi
        : term.meaning_gujarati;

  const isIndic = activeLang === "hi" || activeLang === "gu";

  // First sentence for key insight
  const keyInsight = term.meaning_english
    ?.split(/(?<=[.!?])\s+/)
    .find(s => s.trim().length > 20)
    ?.trim();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      {/* Reading progress bar */}
      <div
        className="fixed left-0 top-0 z-50 h-[3px] bg-gradient-to-r from-primary via-amber-400 to-primary transition-all duration-150"
        style={{ width: `${readingProgress}%` }}
      />

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(220,40%,11%)] via-[hsl(220,38%,16%)] to-[hsl(218,35%,20%)]">
        {/* Decorative blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="bg-primary/25 float absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl" />
          <div className="bg-amber-400/10 float-delayed absolute -bottom-24 -left-24 h-96 w-96 rounded-full blur-3xl" />
        </div>

        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="container relative max-w-5xl py-8 md:py-12">
          {/* Top bar */}
          <div className="mb-8 flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/browse")}
              className="text-white/70 hover:bg-white/10 hover:text-white px-3"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("termDetail.backToBrowse")}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCopyLink}
                className="rounded-full text-white/60 hover:bg-white/10 hover:text-white h-9 w-9"
                title="Copy link"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-400" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                className="rounded-full text-white/60 hover:bg-white/10 hover:text-white h-9 w-9"
                title="Share"
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Title row */}
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            {/* Letter badge */}
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/[0.08] backdrop-blur-sm sm:h-20 sm:w-20">
              <span className="text-primary font-serif text-3xl font-bold md:text-4xl">
                {wordFirstLetter(term.word)}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              {/* Word */}
              <h1 className="break-words font-serif text-4xl font-bold text-white md:text-5xl lg:text-6xl leading-tight mb-2">
                {term.word}
              </h1>

              {/* Script variants */}
              {(term.word_gujarati ||
                term.word_devanagari ||
                term.word_hindi) && (
                <div className="font-devanagari mb-3 flex flex-wrap gap-3 text-lg text-white/60">
                  {term.word_gujarati && (
                    <span>{term.word_gujarati}</span>
                  )}
                  {term.word_devanagari && (
                    <span>{term.word_devanagari}</span>
                  )}
                  {term.word_hindi && <span>{term.word_hindi}</span>}
                </div>
              )}

              {/* Pronunciation */}
              {pronunciationAudioSrc ? (
                <div className="mt-4 w-full max-w-md overflow-hidden rounded-xl border border-white/15 bg-white/[0.07] backdrop-blur-md">
                  <div className="from-primary via-amber-400/80 to-primary h-px bg-gradient-to-r" />
                  <div className="px-4 py-3">
                    <ThemedAudioPlayer
                      src={pronunciationAudioSrc}
                      ariaLabel={t("termDetail.recordedPronunciation")}
                    />
                    {term.pronunciation?.trim() && (
                      <p className="text-white/60 mt-2 flex items-center gap-2 border-t border-white/10 pt-2 text-sm">
                        <Volume2 className="h-3.5 w-3.5 shrink-0" />
                        <span className="italic">/{term.pronunciation.trim()}/</span>
                      </p>
                    )}
                  </div>
                </div>
              ) : term.pronunciation ? (
                <button
                  type="button"
                  onClick={() => {
                    const u = new SpeechSynthesisUtterance(term.word);
                    u.rate = 0.8;
                    u.lang = "en-US";
                    window.speechSynthesis.cancel();
                    window.speechSynthesis.speak(u);
                  }}
                  className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-4 py-1.5 text-sm text-white/65 transition-colors hover:bg-white/15"
                >
                  <Headphones className="h-3.5 w-3.5" />
                  <span className="italic">/{term.pronunciation}/</span>
                </button>
              ) : null}

              {/* Language chips */}
              <div className="mt-4 flex flex-wrap gap-2">
                {term.meaning_english && (
                  <span className="rounded-full bg-blue-500/20 px-3 py-0.5 text-xs font-medium text-blue-300">
                    EN
                  </span>
                )}
                {term.meaning_hindi && (
                  <span className="font-devanagari rounded-full bg-primary/20 px-3 py-0.5 text-xs font-medium text-primary/80">
                    HI · हिंदी
                  </span>
                )}
                {term.meaning_gujarati && (
                  <span className="font-devanagari rounded-full bg-amber-400/20 px-3 py-0.5 text-xs font-medium text-amber-300">
                    GU · ગુજ
                  </span>
                )}
                {pronunciationAudioSrc && (
                  <span className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-0.5 text-xs font-medium text-white/60">
                    <Volume2 className="h-3 w-3" /> Audio
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path
              d="M0 48V28C360 44 720 8 1080 28C1260 37 1350 43 1440 45V48H0Z"
              fill="#f8f7f4"
            />
          </svg>
        </div>
      </section>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <section className="py-10 md:py-14">
        <div className="container max-w-5xl">
          <div className="grid gap-8 lg:grid-cols-[1fr_300px]">

            {/* LEFT — main content */}
            <div className="min-w-0 space-y-6">

              {/* Language tabs */}
              {availableLangs.length > 1 && (
                <div className="flex gap-1 rounded-xl border border-border/50 bg-white p-1 shadow-sm">
                  {availableLangs.map(lang => (
                    <button
                      key={lang.key}
                      onClick={() => setActiveLang(lang.key)}
                      className={cn(
                        "flex-1 rounded-lg py-2 px-3 text-sm font-medium transition-all duration-200",
                        activeLang === lang.key
                          ? "bg-primary text-white shadow-sm"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                      )}
                    >
                      <span className="hidden sm:inline">{lang.label}</span>
                      <span className={cn("sm:hidden font-devanagari", lang.key === "en" && "font-sans")}>
                        {lang.key === "en" ? "EN" : lang.native}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Key Insight callout */}
              {keyInsight && activeLang === "en" && (
                <div className="flex gap-4 rounded-2xl border border-amber-200/60 bg-amber-50/80 p-5 shadow-sm">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                    <Lightbulb className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-700">
                      Key Insight
                    </p>
                    <p className="text-sm leading-relaxed text-amber-900">
                      {keyInsight}
                    </p>
                  </div>
                </div>
              )}

              {/* Meaning card */}
              {activeMeaning && (
                <Card className="overflow-hidden border-border/40 bg-white shadow-sm">
                  {/* Accent bar */}
                  <div className="from-primary via-amber-400/70 to-primary h-[3px] bg-gradient-to-r" />
                  <CardContent className="p-6 md:p-8">
                    <div
                      className={cn(
                        "text-[17px] leading-[1.85] text-foreground/90",
                        isIndic && "font-devanagari"
                      )}
                    >
                      {renderMeaning(activeMeaning, highlightQuery)}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Example Usage */}
              {term.example_usage && (
                <Card className="border-border/40 bg-white shadow-sm overflow-hidden">
                  <CardContent className="p-6 md:p-8">
                    <h3 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      <span className="h-px flex-1 bg-border/50" />
                      {t("termDetail.exampleUsage")}
                      <span className="h-px flex-1 bg-border/50" />
                    </h3>
                    <blockquote className="border-l-[3px] border-primary/40 pl-5">
                      <p className="text-foreground/80 text-[17px] italic leading-[1.85] break-words">
                        "{term.example_usage}"
                      </p>
                    </blockquote>
                  </CardContent>
                </Card>
              )}

              {/* Prev / Next navigation */}
              {(prevTerm || nextTerm) && (
                <div className="grid grid-cols-2 gap-4">
                  {prevTerm ? (
                    <button
                      onClick={() => navigate(`/term/${prevTerm.id}`)}
                      className="group flex items-center gap-3 rounded-xl border border-border/50 bg-white p-4 text-left shadow-sm transition-all hover:border-primary/30 hover:shadow-md"
                    >
                      <ChevronLeft className="text-muted-foreground h-5 w-5 shrink-0 transition-transform group-hover:-translate-x-0.5" />
                      <div className="min-w-0">
                        <p className="text-muted-foreground mb-0.5 text-xs">Previous</p>
                        <p className="text-foreground group-hover:text-primary truncate text-sm font-semibold transition-colors">
                          {prevTerm.word}
                        </p>
                      </div>
                    </button>
                  ) : (
                    <div />
                  )}
                  {nextTerm ? (
                    <button
                      onClick={() => navigate(`/term/${nextTerm.id}`)}
                      className="group flex items-center justify-end gap-3 rounded-xl border border-border/50 bg-white p-4 text-right shadow-sm transition-all hover:border-primary/30 hover:shadow-md"
                    >
                      <div className="min-w-0">
                        <p className="text-muted-foreground mb-0.5 text-xs">Next</p>
                        <p className="text-foreground group-hover:text-primary truncate text-sm font-semibold transition-colors">
                          {nextTerm.word}
                        </p>
                      </div>
                      <ChevronRight className="text-muted-foreground h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              )}
            </div>

            {/* RIGHT — sticky sidebar */}
            <aside className="space-y-5 lg:sticky lg:top-6 lg:self-start">

              {/* Quick Info */}
              <Card className="border-border/40 bg-white shadow-sm overflow-hidden">
                <div className="from-primary/80 to-primary h-[2px] bg-gradient-to-r" />
                <CardContent className="p-5">
                  <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("termDetail.quickInfo")}
                  </h3>
                  <div className="space-y-2.5">
                    {/* First letter */}
                    <div className="flex items-center justify-between rounded-lg bg-secondary/30 px-3 py-2.5">
                      <span className="text-muted-foreground text-sm">
                        {t("termDetail.firstLetter")}
                      </span>
                      <button
                        onClick={() =>
                          navigate(`/browse?letter=${wordFirstLetter(term.word)}`)
                        }
                        className="text-primary flex items-center gap-1 text-sm font-semibold hover:underline"
                      >
                        {wordFirstLetter(term.word)}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    </div>

                    {/* Languages */}
                    <div className="flex items-center justify-between rounded-lg bg-secondary/30 px-3 py-2.5">
                      <span className="text-muted-foreground text-sm">
                        {t("termDetail.languagesLabel")}
                      </span>
                      <span className="text-foreground text-sm font-medium">
                        {[
                          term.meaning_english && "EN",
                          term.meaning_hindi && "HI",
                          term.meaning_gujarati && "GU",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>

                    {/* Audio */}
                    <div className="flex items-center justify-between rounded-lg bg-secondary/30 px-3 py-2.5">
                      <span className="text-muted-foreground text-sm">Audio</span>
                      <span
                        className={cn(
                          "text-sm font-medium",
                          pronunciationAudioSrc
                            ? "text-green-600"
                            : "text-muted-foreground"
                        )}
                      >
                        {pronunciationAudioSrc ? "Available" : "Not available"}
                      </span>
                    </div>

                    {/* Related count */}
                    {filteredRelatedTerms.length > 0 && (
                      <div className="flex items-center justify-between rounded-lg bg-secondary/30 px-3 py-2.5">
                        <span className="text-muted-foreground text-sm">
                          Related Terms
                        </span>
                        <span className="text-foreground text-sm font-medium">
                          {filteredRelatedTerms.length}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Related Terms */}
              {filteredRelatedTerms.length > 0 && (
                <Card className="border-border/40 bg-white shadow-sm overflow-hidden">
                  <CardContent className="p-5">
                    <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("termDetail.relatedTerms")}
                    </h3>
                    <div className="space-y-1.5">
                      {filteredRelatedTerms.slice(0, 6).map(rt => (
                        <button
                          key={rt.id}
                          onClick={() => navigate(`/term/${rt.id}`)}
                          className="group flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition-all hover:bg-primary/5"
                        >
                          <div className="min-w-0">
                            <span className="text-foreground group-hover:text-primary block truncate text-sm font-medium transition-colors">
                              {rt.word}
                            </span>
                            {rt.pronunciation && (
                              <span className="text-muted-foreground text-xs italic">
                                /{rt.pronunciation}/
                              </span>
                            )}
                          </div>
                          <ChevronRight className="text-muted-foreground h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contribute CTA */}
              <Card className="border-border/40 bg-gradient-to-br from-primary/5 to-amber-400/5 shadow-sm overflow-hidden">
                <CardContent className="p-5 text-center">
                  <p className="text-foreground/70 mb-3 text-sm">
                    Know more about this term?
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate("/contribute")}
                    className="w-full border-primary/30 text-primary hover:bg-primary/5 text-sm"
                  >
                    Contribute
                  </Button>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermDetail;
